import React, { FC, useCallback, useMemo, useState } from "react"
import * as Yup from "yup"
import { Button, CircularProgress, IconButton, MenuItem, Modal, Select, TextField } from "@material-ui/core"
import { Formik } from "formik"
import styles from "./styles.module.scss"
import { FormValues } from "./types"
import { Event } from "../../../../../src/types"
import { Game, UserRole } from "@smerf-gaming/types"
import { AutoSizer, Column, Table, TableCellRenderer } from "react-virtualized"
import { Delete } from "@material-ui/icons"

interface RolesModalProps {
  listItem: Event | Game | null
  fetchData: () => void
  hideModal: () => void
  getRolesHook: (object_id?: string | null) => [UserRole[], (object_id: string) => void, boolean]
  addUserRole: (user_id: string, object_id: string, role: string) => Promise<any>
  removeUserRole: (user_id: string, object_id: string) => Promise<any>
  roleOptions: string[]
}

export const RolesModal: FC<RolesModalProps> = ({
  listItem,
  hideModal,
  fetchData,
  getRolesHook,
  addUserRole,
  removeUserRole,
  roleOptions,
}) => {
  const [errorModal, setErrorModal] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [userRoles, getRoles, isLoadingRoles] = getRolesHook(listItem?.id)

  const handleClickAdd = useCallback(
    async (values: FormValues) => {
      if (!listItem?.id) return false

      setIsLoading(true)

      // TODO : add option for other roles in the future
      const response = await addUserRole(values.user_id.trim(), listItem.id, "viewer")

      let success = false

      if (response?.error) {
        setErrorMessage(response?.message || response?.error)
        handleOpenErrorModal()
      } else {
        await getRoles(listItem.id)
        success = true
      }

      setIsLoading(false)
      return success
    },
    [listItem, getRoles],
  )

  const deleteUserRole = useCallback(
    async (values: UserRole) => {
      if (!listItem?.id) return

      setIsLoading(true)

      const response = await removeUserRole(values.user_id.trim(), listItem.id)

      if (response?.error) {
        setErrorMessage(response?.message || response?.error)
        handleOpenErrorModal()
      } else {
        await getRoles(listItem.id)
      }

      setIsLoading(false)
    },
    [listItem, getRoles],
  )

  const handleOpenErrorModal = () => setErrorModal(true)
  const handleCloseErrorModal = () => setErrorModal(false)

  const rowGetter = useCallback(({ index }: { index: number }) => userRoles[index], [userRoles])

  const tableProps = useMemo(
    () => ({
      headerHeight: 44,
      rowHeight: 66,
      height: 500,
      rowClassName: "table-row",
      headerClassName: "table-header",
      rowCount: userRoles.length,
      rowGetter: rowGetter,
    }),
    [userRoles.length, rowGetter],
  )

  const renderPicture: TableCellRenderer = ({ cellData }) => <img src={cellData} className="picture" alt="" />
  const renderDelete: TableCellRenderer = cell => (
    <IconButton aria-label="Delete" onClick={() => deleteUserRole(cell.rowData)}>
      <Delete />
    </IconButton>
  )

  return (
    <div className={styles.modalBody}>
      <h2>Update Roles for: {listItem?.name}</h2>
      <Modal className={styles.modal} open={errorModal} onClose={handleCloseErrorModal}>
        <div className={styles.errorModal}>
          <h2>{errorMessage}</h2>
        </div>
      </Modal>

      <AutoSizer disableHeight style={{ width: 600 }}>
        {({ width }) => (
          <Table width={width} {...tableProps}>
            <Column dataKey="profilePicUrl" width={100} cellRenderer={renderPicture} />
            <Column dataKey="user_id" label="user_id" width={300} />
            <Column dataKey="username" label="Username" width={150} />
            <Column dataKey="role" label="role" width={100} />
            <Column
              dataKey="delete"
              width={50}
              cellRenderer={renderDelete}
              className="buttons-container"
              flexGrow={1}
            />
          </Table>
        )}
      </AutoSizer>

      <Formik
        initialValues={{
          user_id: "",
          role: roleOptions[0],
        }}
        onSubmit={async (values, { resetForm }) => {
          const success = await handleClickAdd(values)
          if (success) {
            resetForm()
          }
        }}
        validationSchema={Yup.object().shape({
          user_id: Yup.string().required(),
          role: Yup.string().required(),
        })}>
        {({ handleChange, handleSubmit, errors, values }) => (
          <>
            <TextField
              className={styles.modalInput}
              required
              label="user_id"
              onChange={handleChange("user_id")}
              placeholder="user_id"
              error={!!errors.user_id}
              value={values.user_id}
              disabled={isLoading}
            />

            <div className={styles.buttonContainer}>
              <Select
                labelId="select-label"
                id="simple-select"
                value={values.role}
                label="Role"
                onChange={handleChange("role")}>
                {roleOptions.map(role => (
                  <MenuItem value={role}>{role}</MenuItem>
                ))}
              </Select>

              {isLoading ? (
                <CircularProgress size={30} />
              ) : (
                <Button variant="contained" size="small" onClick={() => handleSubmit()}>
                  Add
                </Button>
              )}
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}
